import _classPrivateMethodInitSpec from "@babel/runtime/helpers/classPrivateMethodInitSpec";
import _classPrivateFieldInitSpec from "@babel/runtime/helpers/classPrivateFieldInitSpec";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _assertClassBrand from "@babel/runtime/helpers/assertClassBrand";
import _classPrivateFieldGet from "@babel/runtime/helpers/classPrivateFieldGet2";
import _classPrivateFieldSet from "@babel/runtime/helpers/classPrivateFieldSet2";
import { isEmpty, remove } from "lodash";
import { listenerFactory } from "./bindListeners.js";
import { getHistory } from "../history.js";
import { getReadOnlyProxy } from "./proxyFactories.js";
import { mediaEventTarget } from "./mediaQuery.js";
import { mountTree } from "./mount.js";
import { RenderTag } from "./enums.js";
import { unbindTemplateProxy } from "./CustomTemplates/bindTemplateProxy.js";
import { hooks } from "./Runtime.js";
const commonLifeCycles = ["onMount", "onUnmount", "onMediaChange", "onScrollIntoView", "onMessage", "onMessageClose"];
const pageOnlyLifeCycles = ["onBeforePageLoad", "onPageLoad", "onPageLeave", "onBeforePageLeave", "onAnchorLoad", "onAnchorUnload"];
var _routeHelper = /*#__PURE__*/new WeakMap();
var _memoizedLifeCycle = /*#__PURE__*/new WeakMap();
var _observers = /*#__PURE__*/new WeakMap();
var _mediaListener = /*#__PURE__*/new WeakMap();
var _arbitraryLifeCycle = /*#__PURE__*/new WeakMap();
var _initialMenuRequestNode = /*#__PURE__*/new WeakMap();
var _memoizedMenuRequestNodeMap = /*#__PURE__*/new WeakMap();
var _incrementalRenderStates = /*#__PURE__*/new WeakMap();
var _RendererContext_brand = /*#__PURE__*/new WeakSet();
export class RendererContext {
  constructor(scope, options) {
    var _options$unknownBrick;
    _classPrivateMethodInitSpec(this, _RendererContext_brand);
    /**
     * - page: render as whole page, triggering page life cycles.
     * - fragment: render as fragment, not triggering page life cycles.
     */
    _defineProperty(this, "scope", void 0);
    _defineProperty(this, "unknownBricks", void 0);
    _defineProperty(this, "renderId", void 0);
    _classPrivateFieldInitSpec(this, _routeHelper, void 0);
    _classPrivateFieldInitSpec(this, _memoizedLifeCycle, {
      onBeforePageLoad: [],
      onPageLoad: [],
      onPageLeave: [],
      onBeforePageLeave: [],
      onAnchorLoad: [],
      onAnchorUnload: [],
      onMediaChange: [],
      onScrollIntoView: [],
      onMount: [],
      onUnmount: [],
      onMessage: [],
      onMessageClose: []
    });
    _classPrivateFieldInitSpec(this, _observers, new Map());
    _classPrivateFieldInitSpec(this, _mediaListener, void 0);
    _classPrivateFieldInitSpec(this, _arbitraryLifeCycle, new Map());
    _classPrivateFieldInitSpec(this, _initialMenuRequestNode, void 0);
    _classPrivateFieldInitSpec(this, _memoizedMenuRequestNodeMap, new WeakMap());
    _classPrivateFieldInitSpec(this, _incrementalRenderStates, new Map());
    this.scope = scope;
    this.unknownBricks = (_options$unknownBrick = options === null || options === void 0 ? void 0 : options.unknownBricks) !== null && _options$unknownBrick !== void 0 ? _options$unknownBrick : "throw";
    _classPrivateFieldSet(_routeHelper, this, options === null || options === void 0 ? void 0 : options.routeHelper);
    this.renderId = options === null || options === void 0 ? void 0 : options.renderId;
  }
  setInitialMenuRequestNode(menuRequestNode) {
    _classPrivateFieldSet(_initialMenuRequestNode, this, menuRequestNode);
  }
  memoizeMenuRequestNode(routes, menuRequestNode) {
    _classPrivateFieldGet(_memoizedMenuRequestNodeMap, this).set(routes, menuRequestNode);
  }
  getMenuRequests() {
    const menuRequests = [];
    let current = _classPrivateFieldGet(_initialMenuRequestNode, this);
    while (current) {
      if (current.request) {
        menuRequests.push(current.request);
      }
      if (current.child) {
        current = current.child;
      } else {
        var _current;
        while (current && !current.sibling) {
          current = current.return;
        }
        current = (_current = current) === null || _current === void 0 ? void 0 : _current.sibling;
      }
    }
    return menuRequests;
  }
  async reMergeMenuRequestNodes(parentMenuRequestNode, routes) {
    let menuRequestNode = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    const node = _classPrivateFieldGet(_memoizedMenuRequestNodeMap, this).get(routes);
    _classPrivateFieldGet(_memoizedMenuRequestNodeMap, this).set(routes, menuRequestNode);
    let current = parentMenuRequestNode.child;
    let previousSibling;
    while (current) {
      if (current === node) {
        break;
      }
      previousSibling = current;
      current = current.sibling;
    }
    if (previousSibling) {
      previousSibling.sibling = menuRequestNode;
      menuRequestNode.sibling = node === null || node === void 0 ? void 0 : node.sibling;
    } else {
      parentMenuRequestNode.child = menuRequestNode;
    }
    await _classPrivateFieldGet(_routeHelper, this).mergeMenus(this.getMenuRequests());
  }
  reBailout(output) {
    return _classPrivateFieldGet(_routeHelper, this).bailout(output);
  }

  /**
   * Will always resolve when the routing is not the current bootstrap.
   * Otherwise, will throw an error when not bailout.
   *
   * @returns undefined when bailout, or failed output otherwise
   */
  reCatch(error, returnNode) {
    return _classPrivateFieldGet(_routeHelper, this).catch(error, returnNode, false, true);
  }
  async didPerformIncrementalRender(location, prevLocation) {
    let finalResult = false;
    const shouldIgnoreRoutes = [];
    // Perform incremental rendering from inside out.
    // This allows nested incremental sub-routes.
    for (const {
      parentRoutes,
      callback
    } of [..._classPrivateFieldGet(_incrementalRenderStates, this).values()].reverse()) {
      const parentRoute = parentRoutes[parentRoutes.length - 1];
      if (shouldIgnoreRoutes.includes(parentRoute)) {
        // Do not re-render parent routes if any of its children has performed incremental rendering.
        // In the meantime, allow sibling-routes to perform incremental rendering.
        continue;
      }
      const result = await callback(location, prevLocation);
      // When result is true, it means the incremental rendering is performed.
      if (result) {
        shouldIgnoreRoutes.push(...parentRoutes.slice(0, -1));
      }
      // Mark incremental rendering as performed at final if result is true or null.
      if (result !== false) {
        finalResult = true;
      }
    }
    return finalResult;
  }

  /**
   * When `callback` resolved to `true` which means incremental rendering is performed,
   * ignore normal rendering.
   */
  performIncrementalRender(slotConf, parentRoutes, callback) {
    // Override stale incremental render callbacks
    _classPrivateFieldGet(_incrementalRenderStates, this).set(slotConf, {
      parentRoutes,
      callback
    });
  }
  registerBrickLifeCycle(brick, lifeCycle) {
    if (!lifeCycle) {
      return;
    }
    const lifeCycleTypes = [...commonLifeCycles, ...(this.scope === "page" ? pageOnlyLifeCycles : [])];
    for (const key of lifeCycleTypes) {
      const handlers = lifeCycle[key];
      if (handlers) {
        _classPrivateFieldGet(_memoizedLifeCycle, this)[key].push({
          brick,
          handlers: handlers
        });
      }
    }
    // istanbul ignore next
    if (!isEmpty(lifeCycle.useResolves)) {
      // eslint-disable-next-line no-console
      console.error("`lifeCycle.useResolves` is dropped in v3:", lifeCycle);
    }
  }
  registerArbitraryLifeCycle(lifeCycle, fn) {
    const arbitraryCallbacks = _classPrivateFieldGet(_arbitraryLifeCycle, this).get(lifeCycle);
    if (arbitraryCallbacks) {
      arbitraryCallbacks.add(fn);
    } else {
      _classPrivateFieldGet(_arbitraryLifeCycle, this).set(lifeCycle, new Set([fn]));
    }
  }
  reRender(returnNode, node, oldNode) {
    var _ref, _prevLastNormal$eleme, _findNextSiblingNode, _prevLastPortal$eleme, _findNextSiblingNode2;
    // istanbul ignore next: defensive check
    if (returnNode.tag !== RenderTag.ROOT && returnNode.disposed) {
      return;
    }
    const [prevLastNormal, prevLastPortal] = findLastChildNodes(oldNode);
    const insertBeforeChild = (_ref = prevLastNormal ? (_prevLastNormal$eleme = prevLastNormal.element) === null || _prevLastNormal$eleme === void 0 ? void 0 : _prevLastNormal$eleme.nextSibling : (_findNextSiblingNode = findNextSiblingNode(oldNode, false)) === null || _findNextSiblingNode === void 0 ? void 0 : _findNextSiblingNode.element) !== null && _ref !== void 0 ? _ref : null;
    const insertPortalBeforeChildCandidates = [];
    let nextSibling = prevLastPortal ? prevLastPortal === null || prevLastPortal === void 0 || (_prevLastPortal$eleme = prevLastPortal.element) === null || _prevLastPortal$eleme === void 0 ? void 0 : _prevLastPortal$eleme.nextSibling : (_findNextSiblingNode2 = findNextSiblingNode(oldNode, true)) === null || _findNextSiblingNode2 === void 0 ? void 0 : _findNextSiblingNode2.element;
    while (nextSibling) {
      insertPortalBeforeChildCandidates.push(nextSibling);
      // Collect all portal bricks from useBrick, until found a normal portal
      // brick other than from useBrick.
      // Because useBrick could be removed during unmount.
      if (!(nextSibling instanceof HTMLElement && nextSibling.tagName === "DIV")) {
        break;
      }
      nextSibling = nextSibling.nextSibling;
    }

    // Figure out the unchanged prev sibling and next sibling
    let prevSibling;
    let current = returnNode.child;
    while (current && current !== oldNode) {
      if (current.sibling === oldNode) {
        prevSibling = current;
        break;
      }
      current = current.sibling;
    }
    const fragment = document.createDocumentFragment();
    const portalFragment = document.createDocumentFragment();
    const renderRoot = {
      tag: RenderTag.ROOT,
      container: fragment,
      createPortal: portalFragment,
      child: node
    };

    // Override `return`
    current = node;
    while (current) {
      current.return = renderRoot;
      current = current.sibling;
    }

    // Unmount previous brick and abstract nodes, including their descendants
    const [removeBricks, removeAbstracts] = getContainedBrickAndAbstractNodes(oldNode);
    _assertClassBrand(_RendererContext_brand, this, _unmountBricks).call(this, removeBricks);
    _assertClassBrand(_RendererContext_brand, this, _unmountAbstracts).call(this, removeAbstracts);
    mountTree(renderRoot);

    // Connect back
    if (prevSibling) {
      prevSibling.sibling = node;
    } else {
      returnNode.child = node;
    }
    node.sibling = oldNode.sibling;
    _assertClassBrand(_RendererContext_brand, this, _cleanUpNodes).call(this, removeAbstracts);
    _assertClassBrand(_RendererContext_brand, this, _cleanUpNodes).call(this, removeBricks);

    // Resume `return`
    current = node;
    while (current) {
      current.return = returnNode;
      current = current.sibling;
    }
    if (fragment.hasChildNodes()) {
      const entityReturnNode = getEntityReturnNode(returnNode);
      if (entityReturnNode.tag === RenderTag.ROOT) {
        var _entityReturnNode$con;
        (_entityReturnNode$con = entityReturnNode.container) === null || _entityReturnNode$con === void 0 || _entityReturnNode$con.insertBefore(fragment, insertBeforeChild);
      } else {
        var _entityReturnNode$ele;
        (_entityReturnNode$ele = entityReturnNode.element) === null || _entityReturnNode$ele === void 0 || _entityReturnNode$ele.insertBefore(fragment, insertBeforeChild);
      }
    }
    if (portalFragment.hasChildNodes()) {
      var _root;
      let root = node;
      while (root && root.return) {
        root = root.return;
      }
      // istanbul ignore next
      if (((_root = root) === null || _root === void 0 ? void 0 : _root.tag) !== RenderTag.ROOT) {
        throw new Error("Cannot find render root node. This is a bug of Brick Next, please report it.");
      }
      const portal = typeof root.createPortal === "function" ? root.createPortal() : root.createPortal;
      let insertPortalBeforeChild = null;
      for (const candidate of insertPortalBeforeChildCandidates) {
        // Those candidates from useBrick could be removed during unmount.
        // So we need to check if they are still in the portal.
        if (portal.contains(candidate)) {
          insertPortalBeforeChild = candidate;
          break;
        }
      }
      portal.insertBefore(portalFragment, insertPortalBeforeChild);
    }
    const [newBricks] = getContainedBrickAndAbstractNodes(node);
    _assertClassBrand(_RendererContext_brand, this, _initializeRerenderBricks).call(this, newBricks);
  }
  dispose() {
    for (const list of Object.values(_classPrivateFieldGet(_memoizedLifeCycle, this))) {
      list.length = 0;
    }
    for (const list of _classPrivateFieldGet(_observers, this).values()) {
      for (const observer of list) {
        observer.disconnect();
      }
      list.length = 0;
    }
    _classPrivateFieldGet(_observers, this).clear();
    if (_classPrivateFieldGet(_mediaListener, this)) {
      mediaEventTarget.removeEventListener("change", _classPrivateFieldGet(_mediaListener, this));
      _classPrivateFieldSet(_mediaListener, this, undefined);
    }
    _classPrivateFieldGet(_arbitraryLifeCycle, this).clear();
    _classPrivateFieldGet(_incrementalRenderStates, this).clear();
    _classPrivateFieldSet(_memoizedMenuRequestNodeMap, this, undefined);
  }

  // Note: no `onScrollIntoView` and `onMessage`

  dispatchBeforePageLoad() {
    _assertClassBrand(_RendererContext_brand, this, _dispatchGeneralLifeCycle).call(this, "onBeforePageLoad", new CustomEvent("page.beforeLoad"));
  }
  dispatchPageLoad() {
    const event = new CustomEvent("page.load");
    _assertClassBrand(_RendererContext_brand, this, _dispatchGeneralLifeCycle).call(this, "onPageLoad", event);
    // Currently only for e2e testing
    window.dispatchEvent(event);
  }
  dispatchBeforePageLeave(detail) {
    _assertClassBrand(_RendererContext_brand, this, _dispatchGeneralLifeCycle).call(this, "onBeforePageLeave", new CustomEvent("page.beforeLeave", {
      detail
    }));
  }
  dispatchPageLeave() {
    _assertClassBrand(_RendererContext_brand, this, _dispatchGeneralLifeCycle).call(this, "onPageLeave", new CustomEvent("page.leave"));
  }
  dispatchAnchorLoad() {
    const {
      hash
    } = getHistory().location;
    if (hash && hash !== "#") {
      _assertClassBrand(_RendererContext_brand, this, _dispatchGeneralLifeCycle).call(this, "onAnchorLoad", new CustomEvent("anchor.load", {
        detail: {
          hash,
          anchor: hash.substring(1)
        }
      }));
    } else {
      _assertClassBrand(_RendererContext_brand, this, _dispatchGeneralLifeCycle).call(this, "onAnchorUnload", new CustomEvent("anchor.unload"));
    }
  }
  initializeScrollIntoView() {
    for (const {
      brick,
      handlers: conf
    } of _classPrivateFieldGet(_memoizedLifeCycle, this).onScrollIntoView) {
      _assertClassBrand(_RendererContext_brand, this, _addObserver).call(this, brick, conf);
    }
  }
  initializeMediaChange() {
    _classPrivateFieldSet(_mediaListener, this, event => {
      _assertClassBrand(_RendererContext_brand, this, _dispatchGeneralLifeCycle).call(this, "onMediaChange", new CustomEvent("media.change", {
        detail: getReadOnlyProxy(event.detail)
      }));
    });
    mediaEventTarget.addEventListener("change", _classPrivateFieldGet(_mediaListener, this));
  }
  initializeMessageDispatcher() {
    var _hooks$messageDispatc2;
    for (const {
      brick,
      handlers: confList
    } of _classPrivateFieldGet(_memoizedLifeCycle, this).onMessage) {
      for (const conf of [].concat(confList)) {
        var _hooks$messageDispatc;
        hooks === null || hooks === void 0 || (_hooks$messageDispatc = hooks.messageDispatcher) === null || _hooks$messageDispatc === void 0 || _hooks$messageDispatc.onMessage(conf.channel, detail => {
          listenerFactory(conf.handlers, brick.runtimeContext, brick)(new CustomEvent("message.push", {
            detail
          }));
        });
      }
    }
    hooks === null || hooks === void 0 || (_hooks$messageDispatc2 = hooks.messageDispatcher) === null || _hooks$messageDispatc2 === void 0 || _hooks$messageDispatc2.onClose(() => {
      _assertClassBrand(_RendererContext_brand, this, _dispatchGeneralLifeCycle).call(this, "onMessageClose", new CustomEvent("message.close"));
    });
  }
  dispatchOnMount() {
    _assertClassBrand(_RendererContext_brand, this, _dispatchGeneralLifeCycle).call(this, "onMount", new CustomEvent("mount"));
  }
  dispatchOnUnmount() {
    _assertClassBrand(_RendererContext_brand, this, _dispatchGeneralLifeCycle).call(this, "onUnmount", new CustomEvent("unmount"));
  }
}

/** Find the last normal and portal child nodes of the given node. */
function _unmountBricks(bricks) {
  const lifeCycleTypes = [...commonLifeCycles, ...(this.scope === "page" ? pageOnlyLifeCycles : [])];
  const unmountList = [];

  // Clear life cycle handlers, record `onUnmount` at the same time
  for (const key of lifeCycleTypes) {
    const removed = remove(_classPrivateFieldGet(_memoizedLifeCycle, this)[key], item => bricks.has(item.brick));
    if (key === "onUnmount") {
      unmountList.push(...removed);
    }
  }

  // Clear intersection observers
  for (const brick of bricks) {
    var _brick$element, _brick$element2, _brick$disposes;
    const observers = _classPrivateFieldGet(_observers, this).get(brick);
    if (observers !== null && observers !== void 0 && observers.length) {
      for (const observer of observers) {
        observer.disconnect();
      }
      observers.length = 0;
      _classPrivateFieldGet(_observers, this).delete(brick);
    }
    unbindTemplateProxy(brick);
    (_brick$element = brick.element) === null || _brick$element === void 0 || delete _brick$element.$$tplStateStore;
    // Also remove the element
    (_brick$element2 = brick.element) === null || _brick$element2 === void 0 || _brick$element2.remove();
    // Dispose context listeners
    (_brick$disposes = brick.disposes) === null || _brick$disposes === void 0 || _brick$disposes.forEach(dispose => dispose());
    delete brick.disposes;
  }

  // Dispatch unmount events
  const unmountEvent = new CustomEvent("unmount");
  for (const {
    brick,
    handlers
  } of unmountList) {
    listenerFactory(handlers, brick.runtimeContext, brick)(unmountEvent);
  }
}
function _unmountAbstracts(abstracts) {
  for (const item of abstracts) {
    var _item$disposes;
    (_item$disposes = item.disposes) === null || _item$disposes === void 0 || _item$disposes.forEach(dispose => dispose());
    delete item.disposes;
  }
}
function _cleanUpNodes(nodes) {
  for (const node of nodes) {
    delete node.child;
    delete node.sibling;
    delete node.return;
    node.disposed = true;
    if (node.tag === RenderTag.BRICK) {
      delete node.element;
      delete node.runtimeContext;
    }
    Object.freeze(node);
  }
}
function _initializeRerenderBricks(bricks) {
  const mountEvent = new CustomEvent("mount");
  for (const {
    brick,
    handlers
  } of _classPrivateFieldGet(_memoizedLifeCycle, this).onMount) {
    if (bricks.has(brick)) {
      listenerFactory(handlers, brick.runtimeContext, brick)(mountEvent);
    }
  }
  for (const {
    brick,
    handlers: conf
  } of _classPrivateFieldGet(_memoizedLifeCycle, this).onScrollIntoView) {
    if (bricks.has(brick)) {
      _assertClassBrand(_RendererContext_brand, this, _addObserver).call(this, brick, conf);
    }
  }
}
function _dispatchGeneralLifeCycle(type, event) {
  // istanbul ignore next
  if (process.env.NODE_ENV === "development" && this.scope === "fragment" && pageOnlyLifeCycles.includes(type)) {
    throw new Error(`\`lifeCycle.${type}\` cannot be used in ${this.scope}.\nThis is a bug of Brick Next, please report it.`);
  }
  for (const {
    brick,
    handlers
  } of _classPrivateFieldGet(_memoizedLifeCycle, this)[type]) {
    listenerFactory(handlers, brick.runtimeContext, brick)(event);
  }
  const arbitraryCallbacks = _classPrivateFieldGet(_arbitraryLifeCycle, this).get(type);
  if (arbitraryCallbacks) {
    for (const fn of arbitraryCallbacks) {
      fn();
    }
  }
}
function _addObserver(brick, conf) {
  var _conf$threshold;
  const threshold = (_conf$threshold = conf.threshold) !== null && _conf$threshold !== void 0 ? _conf$threshold : 0.1;
  const observer = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        if (entry.intersectionRatio >= threshold) {
          listenerFactory(conf.handlers, brick.runtimeContext, brick)(new CustomEvent("scroll.into.view"));
          observer.disconnect();
        }
      }
    });
  }, {
    threshold
  });
  observer.observe(brick.element);
  let list = _classPrivateFieldGet(_observers, this).get(brick);
  if (!list) {
    list = [];
    _classPrivateFieldGet(_observers, this).set(brick, list);
  }
  list.push(observer);
}
function findLastChildNodes(node) {
  let lastNormal;
  let lastPortal;
  let current = node;
  let level = 0;
  while (current) {
    if (current.tag === RenderTag.BRICK) {
      if (current.portal) {
        lastPortal = current;
      } else {
        lastNormal = current;
      }
    } else if (current.child && current.tag === RenderTag.ABSTRACT) {
      // Only traverse down abstract nodes
      current = current.child;
      level++;
      continue;
    }
    // Level > 0 means we are traversing down abstract nodes.
    // But we do not traverse sibling nodes of the given node.
    if (level <= 0) {
      break;
    }
    if (current.sibling) {
      current = current.sibling;
    } else {
      var _currentReturn;
      // After traversing down abstract nodes, we need to traverse up to find the next sibling.
      let currentReturn = current.return;
      level--;
      while (currentReturn) {
        if (currentReturn.sibling) {
          break;
        }
        currentReturn = currentReturn.return;
        level--;
      }
      if (level <= 0) {
        // Do not traverse up more than the given node.
        break;
      }
      current = (_currentReturn = currentReturn) === null || _currentReturn === void 0 ? void 0 : _currentReturn.sibling;
    }
  }
  return [lastNormal, lastPortal];
}

/** Find the normal or portal sibling node next to the given node (in DOM). */
function findNextSiblingNode(node, portal) {
  let current = node;
  while (current) {
    if (current !== node && current.tag === RenderTag.BRICK && (portal ? current.portal : !current.portal)) {
      return current;
    }
    if (current !== node && current.child && current.tag === RenderTag.ABSTRACT) {
      // Traverse down sibling abstract nodes
      current = current.child;
    } else if (current.sibling) {
      current = current.sibling;
    } else {
      var _currentReturn2;
      // Traverse up abstract nodes to find the next sibling in DOM
      let currentReturn = current.return;
      while (currentReturn) {
        if (currentReturn.tag !== RenderTag.ABSTRACT) {
          // End the loop when encounter a non-abstract return node,
          // it means we can't find the target sibling node.
          return;
        }
        if (currentReturn.sibling) {
          break;
        }
        currentReturn = currentReturn.return;
      }
      current = (_currentReturn2 = currentReturn) === null || _currentReturn2 === void 0 ? void 0 : _currentReturn2.sibling;
    }
  }
}
function getContainedBrickAndAbstractNodes(node) {
  const bricks = new Set();
  const abstracts = new Set();
  let current = node;
  while (current) {
    if (current.tag === RenderTag.BRICK) {
      bricks.add(current);
    } else if (current.tag === RenderTag.ABSTRACT) {
      abstracts.add(current);
    }
    if (current.child) {
      current = current.child;
    } else if (current === node) {
      // End the loop when encounter the original node.
      break;
    } else if (current.sibling) {
      current = current.sibling;
    } else {
      var _currentReturn3;
      let currentReturn = current.return;
      while (currentReturn) {
        if (currentReturn === node) {
          // End the loop when encounter the original node.
          return [bricks, abstracts];
        }
        if (currentReturn.sibling) {
          break;
        }
        currentReturn = currentReturn.return;
      }
      current = (_currentReturn3 = currentReturn) === null || _currentReturn3 === void 0 ? void 0 : _currentReturn3.sibling;
    }
  }
  return [bricks, abstracts];
}
function getEntityReturnNode(node) {
  let current = node;
  while (current.tag === RenderTag.ABSTRACT) {
    current = current.return;
    // istanbul ignore next
    if (!current) {
      throw new Error("Cannot find render root node. This is a bug of Brick Next, please report it.");
    }
  }
  return current;
}
//# sourceMappingURL=RendererContext.js.map